/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { _Window } from 'openfin/_v2/api/window/window';
import { WindowOption } from 'openfin/_v2/api/window/windowOption';
import { Signal } from 'openfin-service-signal';
import { injectable } from 'inversify';
import { Transition, TransitionOptions, Bounds } from 'openfin/_v2/shapes/shapes';

import { WebWindowFactory, WebWindow } from './WebWindow';

@injectable()
export class FinWebWindowFactory implements WebWindowFactory {
  public async createWebWindow(options: WindowOption): Promise<WebWindow> {
    const runtimeVersion = await fin.System.getVersion();
    const apiVersion = parseInt(runtimeVersion.split('.')[2]);

    if (apiVersion >= 50) {
      const finWindow = await fin.Window.create(options);
      const webWindow = new FinWebWindow(finWindow);

      return webWindow;
    } else {
      return this.createLegacyWebWindow(options);
    }
  }

  private async createLegacyWebWindow(options: WindowOption): Promise<WebWindow> {
    // For earlier runtime versions, must rely on the v1 API to create web windows
    const windowV1: fin.OpenFinWindow = await new Promise((resolve, reject) => {
      const win = new fin.desktop.Window(
        options,
        () => {
          resolve(win);
        },
        (error) => {
          reject(error);
        }
      );
    });

    const nativeWindow = windowV1.getNativeWindow();
    const windowV2 = fin.Window.wrapSync({ name: windowV1.name, uuid: windowV1.uuid });
    const webWindow = new FinWebWindow(windowV2, nativeWindow);

    return webWindow;
  }
}

export class FinWebWindow implements WebWindow {
  public readonly onMouseEnter: Signal<[]> = new Signal();
  public readonly onMouseLeave: Signal<[]> = new Signal();
  public readonly onBlurred: Signal<[]> = new Signal();

  private _document: Document;
  private _window: _Window;
  private _nativeWindow: Window;

  private _isActive: boolean;
  private _closePromise!: Promise<void>;

  constructor(window: _Window, nativeWindow?: Window) {
    this._isActive = true;
    this._window = window;

    if (nativeWindow) {
      this._nativeWindow = nativeWindow;
      this._document = nativeWindow.document;
    } else {
      this._nativeWindow = window.getWebWindow();
      this._document = window.getWebWindow().document;
    }

    this.setupListeners();
  }

  public get document(): Document {
    return this._document;
  }

  public get nativeWindow(): Window {
    return this._nativeWindow;
  }

  public async show(): Promise<void> {
    if (this._isActive) {
      return this._window.show();
    }
  }

  public async showAt(left: number, top: number): Promise<void> {
    if (this._isActive) {
      return this._window.showAt(left, top);
    }
  }

  public async hide(): Promise<void> {
    if (this._isActive) {
      return this._window.hide();
    }
  }

  public async setAsForeground(): Promise<void> {
    await this._window.setAsForeground();
    // Focus occurs with `setAsForeground` on Windows but not macOS, so do this for consistency and to make sure we can get a blur event later
    await this._window.focus();
  }

  public async animate(transition: Transition, options: TransitionOptions): Promise<void> {
    if (this._isActive) {
      return this._window.animate(transition, options);
    }
  }

  public async isShowing(): Promise<boolean> {
    return this._window.isShowing();
  }

  public async setBounds(bounds: Bounds): Promise<void> {
    if (this._isActive) {
      return this._window.setBounds(bounds);
    }
  }

  public async getBounds(): Promise<Bounds> {
    return this._window.getBounds();
  }

  public async close(): Promise<void> {
    if (this._isActive) {
      this._isActive = false;
      this._closePromise = this._window.close();
    }

    return this._closePromise;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public async updateOptions(options: object): Promise<void> {
    return this._window.updateOptions(options);
  }

  /**
   * Adds the necessary listeners to the native window/document objects, and ensures they are all removed once
   * they're no longer needed.
   */
  private setupListeners(): void {
    // Bind listeners
    const onEnter = () => this.onMouseEnter.emit();
    const onLeave = () => this.onMouseLeave.emit();
    const onBlurred = () => this.onBlurred.emit();

    // Add listeners
    console.log('Creating', this._window.identity.name);
    this._window.addListener('blurred', onBlurred);
    this._document.addEventListener('mouseenter', onEnter);
    this._document.addEventListener('mouseleave', onLeave);

    // Setup teardown
    this._window.once('closing', () => {
      setImmediate(() => {
        console.log('Closing', this._window.identity.name);
        this._isActive = false;

        this._window.removeListener('blurred', onBlurred).catch();
        this._window = undefined!;

        this._document.removeEventListener('mouseenter', onEnter);
        this._document.removeEventListener('mouseleave', onLeave);
        this._document = undefined!;
        this._nativeWindow = undefined!;
      });
    });
  }
}

Object.assign(window, { FinWebWindow });
